import React, { useState, useEffect } from 'react';
import { Container, Card, Badge, Spinner, Alert, Button, Form } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';

// 「自動」契機は緑、スケジューラ要の日次バッチは青で区別する。
const triggerVariant = (trigger) =>
  trigger && trigger.startsWith('自動') ? 'success' : 'info';

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState({}); // kind -> [messages]
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKind, setBusyKind] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const applyTemplates = (list) => {
    setTemplates(list);
    const d = {};
    list.forEach((t) => { d[t.kind] = [...(t.messages || [])]; });
    setDrafts(d);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const res = await apiClient.get(API_ENDPOINTS.GUEST_TEMPLATES);
        applyTemplates(res.data.templates || []);
        setPlaceholders(res.data.placeholders || []);
      } catch (e) {
        setError('テンプレートの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const updateMessage = (kind, idx, value) => {
    setDrafts((prev) => {
      const arr = [...(prev[kind] || [])];
      arr[idx] = value;
      return { ...prev, [kind]: arr };
    });
  };
  const addMessage = (kind) => {
    setDrafts((prev) => ({ ...prev, [kind]: [...(prev[kind] || []), ''] }));
  };
  const removeMessage = (kind, idx) => {
    setDrafts((prev) => {
      const arr = [...(prev[kind] || [])];
      arr.splice(idx, 1);
      return { ...prev, [kind]: arr };
    });
  };

  const replaceEntry = (entry) => {
    setTemplates((prev) => prev.map((t) => (t.kind === entry.kind ? entry : t)));
    setDrafts((prev) => ({ ...prev, [entry.kind]: [...(entry.messages || [])] }));
  };

  const handleSave = async (kind) => {
    setBusyKind(kind);
    setError('');
    setMessage('');
    try {
      const messages = (drafts[kind] || []).filter((m) => (m || '').trim() !== '');
      if (messages.length === 0) {
        setError('少なくとも1つのメッセージ本文が必要です。');
        return;
      }
      const res = await apiClient.put(`${API_ENDPOINTS.GUEST_TEMPLATES}/${kind}`, { messages });
      replaceEntry(res.data);
      setMessage('テンプレートを保存しました。');
    } catch (e) {
      setError(e.response?.data?.detail || 'テンプレートの保存に失敗しました。');
    } finally {
      setBusyKind('');
    }
  };

  const handleReset = async (kind) => {
    if (!window.confirm('このテンプレートを初期文面に戻しますか？編集内容は失われます。')) return;
    setBusyKind(kind);
    setError('');
    setMessage('');
    try {
      const res = await apiClient.post(`${API_ENDPOINTS.GUEST_TEMPLATES}/${kind}/reset`);
      replaceEntry(res.data);
      setMessage('テンプレートを初期文面に戻しました。');
    } catch (e) {
      setError('初期化に失敗しました。');
    } finally {
      setBusyKind('');
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">案内文テンプレート</h1>
      </div>

      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Alert variant="light" className="small border">
        ゲストへ送る案内文を編集できます。差し込みタグ:&nbsp;
        {placeholders.map((p) => (
          <code key={p} className="me-2">{p}</code>
        ))}
        （送信時に実際の値へ置換されます）
      </Alert>

      {templates.map((t) => (
        <Card key={t.kind} className="mb-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
              <div>
                <Card.Title className="mb-1">
                  {t.label}{' '}
                  {t.customized
                    ? <Badge bg="warning" text="dark">カスタム済み</Badge>
                    : <Badge bg="secondary">既定</Badge>}
                </Card.Title>
                <div className="text-muted small">
                  送信タイミング: {t.timing}{' '}
                  <Badge bg={triggerVariant(t.trigger)} className="ms-1">{t.trigger}</Badge>
                </div>
              </div>
            </div>

            {(drafts[t.kind] || []).map((msg, idx) => (
              <div key={idx} className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">メッセージ {idx + 1}</small>
                  {(drafts[t.kind] || []).length > 1 && (
                    <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeMessage(t.kind, idx)}>
                      削除
                    </Button>
                  )}
                </div>
                <Form.Control
                  as="textarea"
                  rows={Math.min(14, Math.max(4, (msg || '').split('\n').length + 1))}
                  value={msg}
                  onChange={(e) => updateMessage(t.kind, idx, e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
              </div>
            ))}

            <div className="d-flex gap-2 flex-wrap mt-2">
              <Button variant="outline-secondary" size="sm" onClick={() => addMessage(t.kind)}>
                ＋ メッセージを追加
              </Button>
              <Button variant="primary" size="sm" disabled={busyKind === t.kind} onClick={() => handleSave(t.kind)}>
                {busyKind === t.kind ? '保存中...' : '保存'}
              </Button>
              <Button variant="outline-warning" size="sm" disabled={busyKind === t.kind} onClick={() => handleReset(t.kind)}>
                既定に戻す
              </Button>
            </div>

            {t.preview && t.preview.length > 0 && (
              <details className="mt-3">
                <summary className="text-muted small" style={{ cursor: 'pointer' }}>プレビュー（サンプル差し込み）</summary>
                <div className="mt-2">
                  {t.preview.map((p, i) => (
                    <pre key={i} className="bg-light border rounded p-2 small mb-2" style={{ whiteSpace: 'pre-wrap' }}>{p}</pre>
                  ))}
                </div>
              </details>
            )}
          </Card.Body>
        </Card>
      ))}
    </Container>
  );
}

export default Templates;
